import { useState } from "react";
import { motion } from "framer-motion";
import { Wine, LogIn } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";

const errText = (detail) => {
  if (!detail) return "Une erreur est survenue.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || "").join(" ");
  return String(detail);
};

export const Login = ({ onAuth }) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const data = await api.login(username.trim(), password);
      onAuth(data.token, data.user);
    } catch (err) {
      setError(errText(err.response?.data?.detail) || "Connexion impossible");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-md rounded-3xl border border-border bg-card p-7 shadow-2xl"
      >
        <div className="flex items-center gap-3 mb-6">
          <div className="h-12 w-12 rounded-2xl bg-primary flex items-center justify-center">
            <Wine className="h-6 w-6 text-primary-foreground" />
          </div>
          <div>
            <h1 className="font-display text-xl font-black tracking-tight leading-none">Bar Inventory</h1>
            <p className="text-xs text-muted-foreground mt-1">Auberge · Comptage mensuel</p>
          </div>
        </div>
        <h2 className="font-display text-2xl font-extrabold tracking-tight mb-1">Connexion</h2>
        <p className="text-sm text-muted-foreground mb-5">Identifiez-vous pour accéder à l'inventaire.</p>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="login-user" className="text-xs uppercase tracking-wider text-muted-foreground">
              Identifiant
            </Label>
            <Input
              id="login-user"
              data-testid="login-username-input"
              autoFocus
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="email ou identifiant"
              className="mt-2 h-14 text-base rounded-2xl bg-secondary border-border"
            />
          </div>
          <div>
            <Label htmlFor="login-pass" className="text-xs uppercase tracking-wider text-muted-foreground">
              Mot de passe
            </Label>
            <Input
              id="login-pass"
              type="password"
              data-testid="login-password-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="mt-2 h-14 text-base rounded-2xl bg-secondary border-border"
            />
          </div>
          {error && (
            <p data-testid="login-error" className="text-sm text-rose-400">{error}</p>
          )}
          <Button
            type="submit"
            data-testid="login-submit"
            disabled={loading || !username.trim() || !password}
            className="w-full h-14 rounded-2xl text-base font-bold gap-2 active:scale-95 transition-transform"
          >
            <LogIn className="h-5 w-5" />
            {loading ? "Connexion…" : "Se connecter"}
          </Button>
        </form>
      </motion.div>
    </div>
  );
};
